import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SpaceCanvas from '../components/SpaceCanvas';
import Moon from '../components/Moon';

const CONTACT_EMAIL = 'av@mrdn.one';

function MailChip() {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  function onClick(e) {
    e.preventDefault();
    navigator.clipboard
      .writeText(CONTACT_EMAIL)
      .then(() => {
        setCopied(true);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => {
        window.location.href = 'mailto:' + CONTACT_EMAIL;
      });
  }

  return (
    <a
      href={'mailto:' + CONTACT_EMAIL}
      onClick={onClick}
      className="chip flex items-center gap-3 px-7 py-[15px] border border-dotted border-haze/40 rounded-[2px] text-haze hover:border-solid hover:border-haze/80 max-[780px]:px-5"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="22" height="22">
        <rect x="2.5" y="5" width="19" height="14" rx="1.5"></rect>
        <path d="M3 6.5 L12 13 L21 6.5"></path>
      </svg>
      <span className="font-mono text-[13px] font-bold tracking-[1px]">
        {copied ? 'COPIED ✓' : CONTACT_EMAIL.toUpperCase()}
      </span>
    </a>
  );
}

const PIPELINE_STAGES = [
  { letter: 'α', title: 'INGEST', text: 'We see every signal the instant it appears — often before it’s even in a block.' },
  { letter: 'β', title: 'DECODE', text: 'We read it before it’s whole — rebuilt a beat ahead of the validator.' },
  { letter: 'γ', title: 'SIMULATE', text: 'We already know the price — every market, solved in-house.' },
  { letter: 'δ', title: 'EXECUTE', text: 'We fire every route at once — and ours arrives first.', ember: true },
];

const STACK_ITEMS = [
  { title: 'The Metal', text: 'Bare metal, tuned to the packet — nothing between us and the wire.' },
  { title: 'The Data', text: 'The chain read first-hand, from machines we run — never second-hand.' },
  { title: 'The Path', text: 'A straight line to the leaders — no relays, no one else’s latency.', ember: true },
];

export default function Home() {
  return (
    <>
      {/* NAV */}
      <nav className="flex justify-between items-center px-12 py-[18px] border-b border-haze/[0.12] font-mono text-[11px] font-normal tracking-[2px] text-haze/55 relative z-[2] max-[780px]:flex-col max-[780px]:items-stretch max-[780px]:gap-3 max-[780px]:px-5 max-[780px]:py-3.5">
        <span className="text-ice tracking-[3px]">MERIDIAN</span>
        <div className="nav-links flex items-center gap-[26px] max-[780px]:gap-[18px] max-[780px]:overflow-x-auto max-[780px]:whitespace-nowrap max-[780px]:-mx-5 max-[780px]:px-5">
          <a href="#about">ABOUT</a>
          <a href="#pipeline">PIPELINE</a>
          <a href="#stack">STACK</a>
          <a href="#landers">EXECUTION</a>
          <a href="#validators">VALIDATORS</a>
          <a href="https://github.com/mrdnone" target="_blank" rel="noopener noreferrer" title="GitHub" aria-label="GitHub" className="flex items-center">
            <svg viewBox="0 0 16 16" width="15" height="15" fill="currentColor" aria-hidden="true">
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.6 7.6 0 0 1 2-.27c.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8z"></path>
            </svg>
          </a>
          <a href="#contact" title="Contact" aria-label="Contact" className="flex items-center">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" width="15" height="15" aria-hidden="true">
              <rect x="2.5" y="5" width="19" height="14" rx="1.5"></rect>
              <path d="M3 6.5 L12 13 L21 6.5"></path>
            </svg>
          </a>
        </div>
      </nav>

      <main>
        {/* HERO */}
        <div data-hero="3a" className="relative h-[880px] overflow-hidden max-[1020px]:h-auto">
          <SpaceCanvas variant="hero" className="absolute inset-0 w-full h-full" />
          <div className="absolute top-5 left-12 right-12 flex justify-between font-mono text-[10px] font-normal tracking-[3px] text-haze/40 z-[1] max-[780px]:static max-[780px]:px-[22px] max-[780px]:pt-5">
            <span>EST. 2024 · OBSERVATORY OF LATENCY</span>
            <span className="max-[780px]:hidden">CHART Nº 002 — SOLANA SECTOR</span>
          </div>
          <div className="relative z-[1] flex items-center justify-center h-full px-[88px] gap-20 max-w-[1440px] mx-auto max-[1020px]:flex-col max-[1020px]:h-auto max-[1020px]:px-11 max-[1020px]:py-[72px] max-[1020px]:gap-16 max-[780px]:px-[22px] max-[780px]:pt-9 max-[780px]:pb-14 max-[780px]:gap-12">
            <div className="flex flex-col gap-7 max-w-[620px]">
              <div className="font-mono text-[12px] font-normal tracking-[4px] text-haze/50">PROPRIETARY HFT · SOLANA</div>
              <h1 className="font-display text-[104px] leading-[0.95] font-bold tracking-[-2px] m-0 bg-[linear-gradient(180deg,#ffffff_0%,#cdd6ea_38%,#66718c_52%,#f0f4fd_62%,#98a2ba_100%)] bg-clip-text text-transparent [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] max-[1020px]:text-[clamp(58px,10vw,104px)] max-[780px]:text-[clamp(44px,13vw,72px)]">
                MERIDIAN
              </h1>
              <div className="font-mono text-[16px] font-normal leading-[1.85] text-haze/60">
                A proprietary high-frequency trading system on Solana. We own the whole distance from wire to market — and cross it in under a millisecond. An observatory of latency, on the far side of the moon.
              </div>
              <div className="flex gap-4 max-[780px]:flex-col max-[780px]:gap-3">
                <Link to="/access" className="btn-solid max-[780px]:text-center">REQUEST LANDER ACCESS</Link>
                <a href="#pipeline" className="btn-ghost max-[780px]:text-center">THE PIPELINE</a>
              </div>
              <div className="flex gap-9 mt-[18px] font-mono text-[11px] font-normal tracking-[2px] text-haze/45 flex-wrap tabular-nums">
                <span>REACTION <span className="text-ice">&lt;1 MS</span></span>
                <span>LANDING <span className="text-ice">INSTANT</span></span>
                <span>VENUES <span className="text-ice">&gt;100</span></span>
              </div>
            </div>

            <Moon />
          </div>
        </div>

        {/* BODY */}
        <div className="relative">
          <SpaceCanvas variant="body" className="absolute inset-0 w-full h-full z-0" />
          <div className="relative z-[1] max-w-[1440px] mx-auto">
            {/* 01 ABOUT */}
            <div id="about" className="sec">
              <div className="sec-label">01 — ABOUT</div>
              <h2 className="sec-title max-w-[760px] leading-[1.15]">The Line Beneath the Market</h2>
              <div className="font-mono text-[18px] font-normal leading-[1.7] text-ice/[0.86] max-w-[780px] mt-7">
                <span className="text-ice">Meridian</span> trades only its own capital, in the fastest market there is — no clients, no order flow, no conflicts.
              </div>
              <div className="grid grid-cols-2 gap-14 mt-11 font-mono text-[14px] font-normal leading-[1.9] text-haze/55 max-w-[960px] max-[780px]:grid-cols-1 max-[780px]:gap-8">
                <div>A meridian is the invisible line that defines orientation. Ours runs beneath the visible surface of the network — where a millisecond decides everything.</div>
                <div>Every layer is ours: the node, the streams, the decoder, the lander. Built in-house, end to end — because at one millisecond there is no room for anyone else’s.</div>
              </div>
              <div className="h-px bg-haze/[0.12] mt-[52px] mb-5 max-w-[960px]"></div>
              <div className="font-mono text-[11px] font-normal leading-[2] tracking-[2px] text-haze/[0.44] max-w-[960px]">
                EST. 2024&nbsp; · &nbsp;FRANKFURT&nbsp; · &nbsp;RUST SYSTEMS&nbsp; · &nbsp;KERNEL-BYPASS I/O&nbsp; · &nbsp;SOLANA INTERNALS&nbsp; · &nbsp;MARKET MICROSTRUCTURE
              </div>
            </div>

            {/* 02 PIPELINE */}
            <div id="pipeline" className="sec bg-panel/[0.45]">
              <div className="sec-label">02 — PIPELINE</div>
              <h2 className="sec-title">From Signal to Trade, Under 1 ms</h2>
              <div className="sec-sub">
                Solana closes a block every 400 milliseconds. We read, price, and fire in under one — fast enough to land in the very block that revealed the trade.
              </div>
              <div className="flex items-start gap-[22px] mt-16 max-[780px]:flex-col max-[780px]:gap-[26px]">
                <div className="font-mono text-[10px] font-normal tracking-[2px] text-haze/[0.42] whitespace-nowrap pt-px">SIGNAL · T0</div>
                <div className="relative flex-1 grid grid-cols-4 gap-[30px] max-[1020px]:grid-cols-2 max-[780px]:grid-cols-1 max-[780px]:gap-8">
                  <div className="pipe-runner" aria-hidden="true"></div>
                  {PIPELINE_STAGES.map((stage) => (
                    <div key={stage.title}>
                      <div className="flex items-center mb-6">
                        <div
                          className={
                            stage.ember
                              ? 'w-[10px] h-[10px] rounded-full bg-ember shadow-[0_0_16px_rgba(255,138,76,0.9)] shrink-0'
                              : 'w-[9px] h-[9px] rounded-full bg-haze shadow-[0_0_12px_rgba(223,230,245,0.8)] shrink-0'
                          }
                        ></div>
                        <div className="flex-1 h-px bg-haze/[0.16]"></div>
                      </div>
                      <div className={'font-display text-[30px] font-medium leading-none ' + (stage.ember ? 'text-ember' : 'text-haze')}>
                        {stage.letter}
                      </div>
                      <div className="font-mono text-[12px] font-bold tracking-[3px] text-ice mt-3">{stage.title}</div>
                      <div className="font-mono text-[12.5px] font-normal leading-[1.75] text-haze/55 mt-3">{stage.text}</div>
                    </div>
                  ))}
                </div>
                <div className="font-mono text-[10px] font-normal tracking-[2px] text-ember whitespace-nowrap pt-px">FIRED · &lt;1 MS</div>
              </div>
            </div>

            {/* 03 STACK */}
            <div id="stack" className="sec">
              <div className="sec-label">03 — STACK</div>
              <h2 className="sec-title">Built for Solana, Down to the Wire</h2>
              <div className="sec-sub">No managed services, no third-party hops. Three things we never outsource:</div>
              <div className="flex items-start gap-[22px] mt-16 max-[780px]:flex-col max-[780px]:gap-[26px]">
                <div className="font-mono text-[10px] font-normal tracking-[2px] text-haze/[0.42] whitespace-nowrap pt-px">THE WIRE</div>
                <div className="flex-1 relative">
                  <div className="rail-line absolute left-0 right-0 top-1 h-px bg-[linear-gradient(90deg,rgba(223,230,245,0.13),rgba(223,230,245,0.2)_55%,rgba(255,138,76,0.6))]"></div>
                  <div className="grid grid-cols-3 gap-[44px] max-[780px]:grid-cols-1 max-[780px]:gap-8">
                    {STACK_ITEMS.map((item) => (
                      <div key={item.title}>
                        <div
                          className={
                            item.ember
                              ? 'relative w-[10px] h-[10px] rounded-full bg-ember shadow-[0_0_16px_rgba(255,138,76,0.9)] mb-[26px]'
                              : 'relative w-[9px] h-[9px] rounded-full bg-haze shadow-[0_0_12px_rgba(223,230,245,0.8)] mb-[26px]'
                          }
                        ></div>
                        <div className="font-display text-[28px] font-medium text-ice leading-none">{item.title}</div>
                        <div className="font-mono text-[13px] font-normal leading-[1.8] text-haze/55 mt-3.5">{item.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="font-mono text-[10px] font-normal tracking-[2px] text-ember whitespace-nowrap pt-px">THE MARKET</div>
              </div>
            </div>

            {/* 04 EXECUTION */}
            <div id="landers" className="sec bg-panel/[0.45]">
              <div className="sec-label">04 — EXECUTION</div>
              <h2 className="sec-title">Parallel Delivery. First to Land Wins.</h2>
              <div className="sec-sub">One opportunity, every route, fired at once.</div>
              <div className="grid grid-cols-[1.2fr_1fr] gap-6 mt-14 items-stretch max-[780px]:grid-cols-1 max-[780px]:gap-8">
                <div className="p-[40px_36px] border border-solid border-haze/55 rounded-[2px] relative bg-[linear-gradient(180deg,rgba(223,230,245,0.08),rgba(223,230,245,0.015))] flex flex-col gap-4">
                  <div className="absolute -top-1.5 left-8 w-[11px] h-[11px] rounded-full bg-ember shadow-[0_0_16px_rgba(255,138,76,0.9)]"></div>
                  <div className="font-mono text-[10px] font-normal tracking-[3px] text-haze/50">FLAGSHIP · IN-HOUSE</div>
                  <div className="font-display text-[26px] font-bold text-white tracking-[1px]">MERIDIAN LANDER</div>
                  <div className="font-mono text-[13.5px] font-normal leading-[1.8] text-haze/60">
                    Our private lander exists to answer one question: which route lands first. It beats every public lander we benchmark — and our own book trades through it, every day.
                  </div>
                  <Link to="/access" className="btn-solid self-start !px-[30px] !py-3.5 mt-2">REQUEST ACCESS →</Link>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="font-mono text-[10px] font-normal tracking-[3px] text-haze/50 mb-1">PARALLEL FAN-OUT · ALL PATHS AT ONCE</div>
                  <div className="font-mono text-[14px] font-normal leading-[2.15] tracking-[1px] text-haze/[0.68]">
                    Jito&nbsp; · &nbsp;Helius&nbsp; · &nbsp;Falcon&nbsp; · &nbsp;Blockrazor&nbsp; · &nbsp;Flashblock&nbsp; · &nbsp;Astralane&nbsp; · &nbsp;bloXroute&nbsp; · &nbsp;Nozomi&nbsp; · &nbsp;Rakurai&nbsp; · &nbsp;Harmonic&nbsp; · &nbsp;ZeroSlot&nbsp; · &nbsp;Soyas&nbsp; · &nbsp;Direct&nbsp;TPU&nbsp; · &nbsp;SWQoS
                    <span className="text-haze/35">&nbsp; · &nbsp;and whatever ships next</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 05 VALIDATORS */}
            <div id="validators" className="sec">
              <div className="sec-label">05 — VALIDATORS</div>
              <h2 className="sec-title">We Speak Every Client’s Language</h2>
              <div className="sec-sub">
                Every client on mainnet, every preconfirmation stream that exists. If you run a validator, there’s a straightforward partnership on the table.
              </div>
              <div className="font-display text-[22px] font-medium leading-[1.9] text-ice mt-10 max-w-[860px]">
                Agave&nbsp; · &nbsp;Jito&nbsp; · &nbsp;BAM&nbsp; · &nbsp;Firedancer&nbsp; · &nbsp;Frankendancer&nbsp; · &nbsp;Harmonic&nbsp; · &nbsp;Rakurai
                <span className="text-haze/35">&nbsp; · &nbsp;and more</span>
              </div>
              <a href="#contact" className="btn-ghost inline-block mt-11">DISCUSS PARTNERSHIP →</a>
            </div>

            {/* 06 VENUES */}
            <div className="sec bg-panel/[0.45]">
              <div className="sec-label">06 — VENUES</div>
              <h2 className="sec-title">Every Venue, Including the Dark Ones</h2>
              <div className="sec-sub max-w-[760px]">
                Atomic arbitrage across every public AMM — and the private venues most systems never see. One transaction, in and out.
              </div>
              <div className="grid grid-cols-2 gap-6 mt-14 max-[780px]:grid-cols-1 max-[780px]:gap-8">
                <div className="card border border-solid border-haze/45">
                  <div className="card-node !bg-white !shadow-[0_0_14px_rgba(255,255,255,0.9)]"></div>
                  <div className="font-mono text-[14px] font-bold tracking-[2px] text-white mb-2">PROP AMMs</div>
                  <div className="font-mono text-[12px] font-normal leading-[1.7] text-haze/45 mb-[18px]">
                    Modeled in full, priced in-house — depth and flow the market doesn’t publish.
                  </div>
                  <div className="font-mono text-[13px] font-normal leading-[2] tracking-[1px] text-ice">
                    SolFi&nbsp; · &nbsp;GoonFi&nbsp; · &nbsp;HumidiFi&nbsp; · &nbsp;Tessera&nbsp;V&nbsp; · &nbsp;Aquifer&nbsp; · &nbsp;BisonFi
                  </div>
                </div>
                <div className="card">
                  <div className="card-node"></div>
                  <div className="font-mono text-[14px] font-bold tracking-[2px] text-ice mb-2">CLASSIC VENUES</div>
                  <div className="font-mono text-[12px] font-normal leading-[1.7] text-haze/45 mb-[18px]">
                    Every major public AMM on Solana, replicated down to its math.
                  </div>
                  <div className="font-mono text-[13px] font-normal leading-[2] tracking-[1px] text-haze/60">
                    Orca&nbsp; · &nbsp;Raydium&nbsp; · &nbsp;Meteora&nbsp; · &nbsp;Pumpswap&nbsp; · &nbsp;Lifinity&nbsp; · &nbsp;Phoenix&nbsp; · &nbsp;Openbook
                    <span className="text-haze/35">&nbsp; · &nbsp;and more</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 07 OPEN SOURCE */}
            <div className="sec">
              <div className="flex justify-between items-center gap-12 max-[780px]:flex-col max-[780px]:items-start max-[780px]:gap-7">
                <div>
                  <div className="sec-label">07 — OPEN SOURCE</div>
                  <h2 className="sec-title !text-[40px]">Giving Back to the Ecosystem</h2>
                  <div className="sec-sub max-w-[640px] !text-[14.5px]">
                    Tools we built because we needed them, released because others need them too — for validators, researchers, and operators.
                  </div>
                  <div className="flex items-center gap-2.5 mt-6 font-mono text-[11px] font-normal tracking-[2px]">
                    <span className="live-dot"></span>
                    <a href="https://solana.mrdn.one/blacklist" target="_blank" rel="noopener noreferrer" className="text-ice">
                      SOLANA BLACKLIST EXPLORER ↗
                    </a>
                    <span className="text-haze/40">LIVE · PUBLIC API</span>
                  </div>
                </div>
                <Link to="/opensource" className="btn-solid shrink-0 !px-[34px] !py-4">VIEW THE REGISTRY ↗</Link>
              </div>
            </div>

            {/* 08 CONTACT */}
            <div id="contact" className="sec bg-panel/[0.45] !pb-0 max-[780px]:!pb-[60px]">
              <div className="sec-label">08 — CONTACT</div>
              <div className="grid grid-cols-2 gap-16 items-start max-[780px]:grid-cols-1 max-[780px]:gap-8">
                <div>
                  <h2 className="sec-title !text-[40px] mb-[18px]">Reach the Desk</h2>
                  <div className="font-mono text-[14.5px] font-normal leading-[1.9] text-haze/55 max-w-[480px]">
                    Lander access, a preconf partnership, or backing the fastest path on Solana — whatever brings you here, write to us. We read everything.
                  </div>
                </div>
                <div className="flex flex-col gap-8">
                  <div className="flex gap-4 flex-wrap">
                    <MailChip />
                    <a
                      href="https://t.me/mrdnone"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-contact="telegram"
                      className="chip flex items-center gap-3 px-7 py-[15px] border border-dotted border-haze/40 rounded-[2px] text-haze hover:border-solid hover:border-haze/80"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden="true">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"></path>
                      </svg>
                      <span className="font-mono text-[13px] font-bold tracking-[1px]">TELEGRAM</span>
                    </a>
                    <a
                      href="https://discord.com/users/onlineonline11_21910"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-contact="discord"
                      className="chip flex items-center gap-3 px-7 py-[15px] border border-dotted border-haze/40 rounded-[2px] text-haze hover:border-solid hover:border-haze/80"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden="true">
                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"></path>
                      </svg>
                      <span className="font-mono text-[13px] font-bold tracking-[1px]">DISCORD</span>
                    </a>
                  </div>
                  <div className="flex gap-14 flex-wrap max-[780px]:gap-7">
                    <div>
                      <div className="font-mono text-[10px] font-normal tracking-[3px] text-haze/40 mb-2">LOCATION</div>
                      <div className="font-mono text-[14px] font-normal text-ice">Frankfurt, Germany</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] font-normal tracking-[3px] text-haze/40 mb-2">SPECIALIZATION</div>
                      <div className="font-mono text-[14px] font-normal text-ice">Solana HFT · Low-Latency Systems · Validator Ops</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-between items-center mt-[88px] pt-[26px] pb-8 border-t border-haze/[0.12] font-mono text-[10.5px] font-normal tracking-[2px] text-haze/40 max-[780px]:flex-wrap max-[780px]:gap-2.5">
                <span>© 2026 MERIDIAN. ALL RIGHTS RESERVED.</span>
                <span>CHART Nº 002 — SOLANA SECTOR · FRANKFURT STATION</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
